import { Link, useParams } from "react-router-dom";
import { useState, useEffect } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import SEOHead from "@/components/feature/SEOHead";
import SchemaOrg from "@/components/feature/SchemaOrg";
import { movies, categories } from "@/mocks/movies";
import type { Movie } from "@/mocks/movies";

const sortOptions = [
  { value: "default", label: "默认排序" },
  { value: "rating", label: "评分最高" },
  { value: "year", label: "最新发布" },
  { value: "title", label: "按名称" },
];

function MovieCard({ movie }: { movie: Movie }) {
  const categoryName = categories.find((c) => c.slug === movie.category)?.name || movie.category;
  return (
    <Link
      to={`/article/${movie.id}`}
      className="group block bg-background-50 rounded-xl overflow-hidden border border-background-200 hover:border-accent-300 transition-all duration-300 cursor-pointer"
    >
      <div className="relative w-full aspect-[3/4] overflow-hidden">
        <img
          src={movie.posterUrl}
          alt={movie.title}
          title={`${movie.title} - 屁屁影院在线观看`}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent"></div>
        <div className="absolute top-3 left-3 px-2 py-1 bg-accent-500 text-background-50 text-xs font-medium rounded-md">
          {categoryName}
        </div>
        <div className="absolute top-3 right-3 flex items-center gap-1 px-2 py-1 bg-black/60 text-amber-400 text-xs font-semibold rounded-md">
          <i className="ri-star-fill text-[10px]"></i>
          {movie.rating}
        </div>
        <div className="absolute bottom-3 left-3 right-3">
          <h3 className="text-base font-semibold text-white truncate font-heading">
            {movie.title}
          </h3>
          <p className="text-xs text-white/70 mt-1 truncate">
            {movie.year} / {movie.genre.slice(0, 2).join(" ")}
          </p>
        </div>
      </div>
    </Link>
  );
}

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const currentCategory = categories.find((c) => c.slug === slug) || categories[0];
  const [sortBy, setSortBy] = useState("default");
  const [showSort, setShowSort] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [slug]);

  const categoryMovies = movies.filter((m) => m.category === currentCategory.slug);

  const sortedMovies = [...categoryMovies].sort((a, b) => {
    switch (sortBy) {
      case "rating":
        return b.rating - a.rating;
      case "year":
        return b.year - a.year;
      case "title":
        return a.title.localeCompare(b.title, "zh");
      default:
        return b.year - a.year;
    }
  });

  const categoryNames: Record<string, string> = {
    dianying: "电影",
    dianshiju: "电视剧",
    zongyi: "综艺",
    dongman: "动漫",
  };

  const categoryKeywords: Record<string, string> = {
    dianying: "电影,免费电影,高清电影,最新电影,在线电影",
    dianshiju: "电视剧,免费电视剧,高清电视剧,最新电视剧,热门电视剧",
    zongyi: "综艺,免费综艺,热门综艺,最新综艺,真人秀",
    dongman: "动漫,免费动漫,高清动漫,最新动漫,日本动漫,国产动漫",
  };

  const title = `屁屁影院${categoryNames[currentCategory.slug] || ""} - ${currentCategory.name}在线观看`;
  const desc = `屁屁影院${currentCategory.name}频道提供海量${currentCategory.name}资源免费在线观看，每日更新最新最热门的${currentCategory.name}，支持高清画质流畅播放，无需注册即可畅享。`;
  const keywords = `屁屁影院,${categoryKeywords[currentCategory.slug] || ""},屁屁影院${currentCategory.name}`;

  return (
    <>
      <SEOHead
        title={title}
        description={desc}
        keywords={keywords}
        canonicalUrl={`https://www.pipicinema.com/category/${currentCategory.slug}`}
      />
      <SchemaOrg
        type="BreadcrumbList"
        data={{
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "首页", item: "https://www.pipicinema.com/" },
            { "@type": "ListItem", position: 2, name: currentCategory.name },
          ],
        }}
      />
      <SchemaOrg
        type="WebPage"
        data={{
          name: title,
          url: `https://www.pipicinema.com/category/${currentCategory.slug}`,
          description: desc,
          breadcrumb: { "@id": `https://www.pipicinema.com/category/${currentCategory.slug}#breadcrumb` },
        }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="max-w-[1400px] mx-auto px-4 md:px-6 pt-24 pb-14">
          <Breadcrumb
            items={[
              { label: "首页", path: "/" },
              { label: currentCategory.name },
            ]}
          />

          {/* Category Header */}
          <div className="relative w-full h-[250px] md:h-[350px] rounded-2xl overflow-hidden mb-10">
            <img
              src={sortedMovies[0]?.bannerUrl || sortedMovies[0]?.heroUrl}
              alt={currentCategory.name}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/60"></div>
            <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-accent-500/90">
                  <i className={`${currentCategory.icon} text-background-50 text-lg`}></i>
                </div>
                <h1 className="text-2xl md:text-3xl font-bold text-white font-heading">
                  {currentCategory.name}
                </h1>
              </div>
              <p className="text-white/70 text-sm md:text-base">
                共 {categoryMovies.length} 部作品
              </p>
            </div>
          </div>

          {/* Category Tabs */}
          <div className="flex items-center gap-3 mb-8 overflow-x-auto pb-2 flex-wrap">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  cat.slug === currentCategory.slug
                    ? "bg-accent-500 text-background-50"
                    : "bg-background-100 text-foreground-600 hover:bg-background-200"
                }`}
              >
                <i className={cat.icon}></i>
                {cat.name}
              </Link>
            ))}
          </div>

          {/* Filter Bar */}
          <div className="flex items-center justify-between mb-6">
            <p className="text-sm text-foreground-600">
              共找到 <strong className="text-foreground-950">{categoryMovies.length}</strong> 部
              <span className="hidden sm:inline"> {currentCategory.name}</span>
            </p>
            <div className="relative">
              <button
                onClick={() => setShowSort(!showSort)}
                className="flex items-center gap-2 px-4 py-2 bg-background-100 rounded-lg text-sm text-foreground-700 hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-arrow-up-down-line"></i>
                {sortOptions.find((o) => o.value === sortBy)?.label}
                <i className="ri-arrow-down-s-line"></i>
              </button>
              {showSort && (
                <div className="absolute right-0 top-full mt-2 w-36 bg-background-50 border border-background-200 rounded-lg shadow-lg z-20 py-1">
                  {sortOptions.map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => {
                        setSortBy(opt.value);
                        setShowSort(false);
                      }}
                      className={`w-full text-left px-4 py-2 text-sm transition-colors cursor-pointer whitespace-nowrap ${
                        sortBy === opt.value
                          ? "text-accent-600 bg-accent-50"
                          : "text-foreground-700 hover:bg-background-100"
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Movie Grid */}
          {sortedMovies.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
              {sortedMovies.map((movie) => (
                <MovieCard key={movie.id} movie={movie} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
                <i className="ri-emotion-sad-line text-3xl"></i>
              </div>
              <p className="text-foreground-600">暂无{currentCategory.name}资源</p>
            </div>
          )}
        </main>

        <Footer />
      </div>
    </>
  );
}